<!-- src/components/BoardRecipePicker.vue -->
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { GET_ALL_RECIPE_CATEGORIES, GET_RECIPES } from '../graphql/queries'
import { useBodyScrollLock } from '../composables/useBodyScrollLock'

const props = defineProps<{ mode: 'sidebar' | 'sheet'; open?: boolean }>()

const emit = defineEmits<{
  'select-recipe': [recipe: { id: string; title: string; category?: { id: string; name: string } }]
  close: []
}>()

useBodyScrollLock(() => props.mode === 'sheet' && !!props.open)

const searchQuery = ref('')
const selectedCategoryId = ref('')
const sheetSearchInput = ref<HTMLInputElement | null>(null)

const onDocKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && props.mode === 'sheet' && props.open) emit('close')
}
onMounted(() => document.addEventListener('keydown', onDocKeydown))
onBeforeUnmount(() => document.removeEventListener('keydown', onDocKeydown))

// Move focus into the sheet when it opens, for keyboard/screen-reader users.
watch(
  () => props.open,
  async (open) => {
    if (props.mode === 'sheet' && open) {
      await nextTick()
      sheetSearchInput.value?.focus()
    }
  }
)

const { result: recipesResult, loading: loadingRecipes } = useQuery(GET_RECIPES, {
  limit: 100,
  page: 0,
  search: '',
})
const { result: categoriesResult } = useQuery(GET_ALL_RECIPE_CATEGORIES)

const recipes = computed(() => recipesResult.value?.recipes || [])
const categories = computed(() => categoriesResult.value?.getAllRecipeCategories || [])

const filteredRecipes = computed(() => {
  let filtered = recipes.value

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter((recipe: any) =>
      recipe.title.toLowerCase().includes(query) ||
      recipe.description?.toLowerCase().includes(query)
    )
  }

  if (selectedCategoryId.value) {
    filtered = filtered.filter((recipe: any) => recipe.category?.id === selectedCategoryId.value)
  }

  return filtered
})

const getInitials = (title: string) =>
  title
    .split(' ')
    .filter((w) => w && /^[A-ZÀ-Ú]/u.test(w))
    .map((w) => w[0].toUpperCase())
    .slice(0, 3)
    .join('')

const onDragStart = (e: DragEvent, recipe: { id: string; title: string }) => {
  e.dataTransfer!.effectAllowed = 'copy'
  e.dataTransfer!.setData('text/plain', JSON.stringify({ id: recipe.id, title: recipe.title }))
}
</script>

<template>
  <!-- Desktop: persistent sidebar, drag a recipe onto the board -->
  <div v-if="props.mode === 'sidebar'" class="picker-card">
    <h3 class="picker-title">Recipe Library</h3>
    <p class="picker-hint">Drag a recipe onto the board →</p>

    <div class="search-wrap">
      <svg class="search-icon" width="16" height="16" fill="none" viewBox="0 0 24 24">
        <path d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" stroke="#94A3B8" stroke-width="2" stroke-linecap="round" />
      </svg>
      <input v-model="searchQuery" type="text" placeholder="Search recipes..." class="search-input" />
    </div>

    <div class="filter-row">
      <button @click="selectedCategoryId = ''" class="filter-pill" :class="{ active: selectedCategoryId === '' }">
        All
      </button>
      <button
        v-for="cat in categories"
        :key="cat.id"
        @click="selectedCategoryId = selectedCategoryId === cat.id ? '' : cat.id"
        class="filter-pill"
        :class="{ active: selectedCategoryId === cat.id }"
      >
        {{ cat.name }}
      </button>
    </div>

    <div class="recipe-list">
      <div v-if="loadingRecipes" class="picker-empty">Loading recipes...</div>
      <div v-else-if="filteredRecipes.length === 0" class="picker-empty">No recipes found</div>
      <div
        v-for="recipe in filteredRecipes"
        :key="recipe.id"
        class="recipe-row"
        draggable="true"
        @dragstart="onDragStart($event, recipe)"
      >
        <div class="recipe-avatar">
          <span class="recipe-initials">{{ getInitials(recipe.title) }}</span>
        </div>
        <div class="recipe-info">
          <p class="recipe-title">{{ recipe.title }}</p>
          <p v-if="recipe.category" class="recipe-category">{{ recipe.category.name }}</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Mobile: bottom sheet, tap a recipe's "+" to add it -->
  <Teleport v-else to="body">
    <div v-if="props.open" class="sheet-overlay" @click.self="emit('close')">
      <div class="sheet-panel" role="dialog" aria-modal="true" aria-labelledby="board-picker-title">
        <div class="sheet-handle"></div>
        <h3 id="board-picker-title" class="sheet-title">Choose a recipe</h3>

        <div class="search-wrap">
          <svg class="search-icon" width="16" height="16" fill="none" viewBox="0 0 24 24">
            <path d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" stroke="#94A3B8" stroke-width="2" stroke-linecap="round" />
          </svg>
          <input ref="sheetSearchInput" v-model="searchQuery" type="text" placeholder="Search recipes..." class="search-input" />
        </div>

        <div class="filter-row">
          <button @click="selectedCategoryId = ''" class="filter-pill" :class="{ active: selectedCategoryId === '' }">
            All
          </button>
          <button
            v-for="cat in categories"
            :key="cat.id"
            @click="selectedCategoryId = selectedCategoryId === cat.id ? '' : cat.id"
            class="filter-pill"
            :class="{ active: selectedCategoryId === cat.id }"
          >
            {{ cat.name }}
          </button>
        </div>

        <div class="recipe-list sheet-recipe-list">
          <div v-if="loadingRecipes" class="picker-empty">Loading recipes...</div>
          <div v-else-if="filteredRecipes.length === 0" class="picker-empty">No recipes found</div>
          <div v-for="recipe in filteredRecipes" :key="recipe.id" class="recipe-row recipe-row--sheet">
            <div class="recipe-avatar">
              <span class="recipe-initials">{{ getInitials(recipe.title) }}</span>
            </div>
            <div class="recipe-info">
              <p class="recipe-title">{{ recipe.title }}</p>
              <p v-if="recipe.category" class="recipe-category">{{ recipe.category.name }}</p>
            </div>
            <button
              class="recipe-add-btn"
              :aria-label="`Add ${recipe.title}`"
              @click="emit('select-recipe', { id: recipe.id, title: recipe.title, category: recipe.category })"
            >+</button>
          </div>
        </div>

        <button class="sheet-cancel" @click="emit('close')">Cancel</button>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.picker-card {
  background: white;
  border-radius: 20px;
  padding: 20px;
  border: 1px solid #F1F5F9;
}
.picker-title { font-family: Inter, sans-serif; font-weight: 800; font-size: 18px; color: #0F172A; }
.picker-hint { font-size: 12px; color: #94A3B8; margin: 4px 0 16px; }

.search-wrap { position: relative; margin-bottom: 12px; }
.search-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); }
.search-input {
  width: 100%;
  height: 42px;
  border-radius: 12px;
  border: 1px solid #E2E8F0;
  background: white;
  padding: 0 14px 0 40px;
  font-size: 13px;
  font-family: Inter, sans-serif;
  outline: none;
}
.search-input:focus { border-color: #4F8646; }

.filter-row { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 16px; }
.filter-pill {
  height: 30px;
  border-radius: 9999px;
  border: 1.5px solid #E2E8F0;
  background: white;
  color: #64748B;
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 11px;
  padding: 0 14px;
  cursor: pointer;
  transition: all 150ms ease;
}
.filter-pill.active { background: #4F8646; color: white; border-color: #4F8646; }

.recipe-list { display: flex; flex-direction: column; gap: 8px; max-height: 60vh; overflow-y: auto; }
.picker-empty { text-align: center; font-size: 13px; color: #94A3B8; padding: 24px 0; }

.recipe-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border-radius: 12px;
  border: 1px solid #F1F5F9;
  background: #F8FAFC;
  cursor: grab;
  transition: background-color 150ms ease, opacity 150ms ease;
}
.recipe-row:hover { background: #EAF1EB; }
.recipe-row:active { cursor: grabbing; opacity: 0.7; }
.recipe-row--sheet { cursor: default; }
.recipe-row--sheet:active { opacity: 1; }

.recipe-avatar {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #E2E8F0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.recipe-initials { font-size: 12px; font-weight: 800; color: #94A3B8; letter-spacing: 0.02em; }

.recipe-info { flex: 1; min-width: 0; }
.recipe-title { font-family: Inter, sans-serif; font-weight: 700; font-size: 13px; color: #0F172A; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.recipe-category { font-size: 11px; color: #94A3B8; margin-top: 2px; }

.recipe-add-btn {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #4F8646;
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
}
.recipe-add-btn:hover { background: rgba(79, 134, 70, 0.1); }

/* ── Bottom sheet (mobile) ─────────────────────────────────────────────── */
.sheet-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: flex-end;
  z-index: 60;
}
.sheet-panel {
  width: 100%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 24px 24px 0 0;
  padding: 12px 20px 20px;
  overflow: hidden;
}
.sheet-handle {
  width: 40px;
  height: 4px;
  border-radius: 9999px;
  background: #E2E8F0;
  margin: 0 auto 16px;
  flex-shrink: 0;
}
.sheet-title { font-family: Inter, sans-serif; font-weight: 800; font-size: 20px; color: #0F172A; margin-bottom: 16px; flex-shrink: 0; }
.sheet-recipe-list { flex: 1; min-height: 0; max-height: none; }
.sheet-cancel {
  flex-shrink: 0;
  margin-top: 16px;
  height: 48px;
  border-radius: 12px;
  border: none;
  background: #EEF2F6;
  color: #334155;
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
}
</style>
